import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, MapPin, Clock, Pencil, X, Users, Search, CheckSquare, Square, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { ConfirmDeleteDialog } from "../../components/ConfirmDeleteDialog";

const inputCls = "bg-[#0F0F0F] border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#D4AF37]";

const CentreForm = ({ initial, onSave, onCancel }) => {
  const [f, setF] = useState(initial || { name: "", address: "" });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-4 grid gap-3" data-testid="centre-edit-form">
      <input required placeholder="Centre name *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="centre-name" />
      <input placeholder="Address (optional)" value={f.address || ""} onChange={(e) => setF({ ...f, address: e.target.value })} className={inputCls} data-testid="centre-address" />
      <div className="flex gap-2">
        <button type="submit" className="flex-1 bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm hover:bg-[#F3D059]" data-testid="centre-save">Save</button>
        {onCancel && <button type="button" onClick={onCancel} className="border border-white/20 text-white/70 text-xs uppercase tracking-widest px-4 py-2.5 rounded-sm">Cancel</button>}
      </div>
    </form>
  );
};

const BatchForm = ({ initial, centres, onSave, onCancel }) => {
  const [f, setF] = useState(initial || { name: "", centre: "", days: "", timings: "" });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-4 grid gap-3" data-testid="batch-edit-form">
      <input required placeholder="Batch name *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="batch-name" />
      <select value={f.centre || ""} onChange={(e) => setF({ ...f, centre: e.target.value })} className={inputCls} data-testid="batch-centre">
        <option value="">Centre (optional)</option>
        {centres.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
      </select>
      <div className="grid grid-cols-2 gap-3">
        <input placeholder="Days (e.g. MWF)" value={f.days || ""} onChange={(e) => setF({ ...f, days: e.target.value })} className={inputCls} data-testid="batch-days" />
        <input placeholder="Timings" value={f.timings || ""} onChange={(e) => setF({ ...f, timings: e.target.value })} className={inputCls} data-testid="batch-timings" />
      </div>
      <div className="flex gap-2">
        <button type="submit" className="flex-1 bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm hover:bg-[#F3D059]" data-testid="batch-save">Save</button>
        {onCancel && <button type="button" onClick={onCancel} className="border border-white/20 text-white/70 text-xs uppercase tracking-widest px-4 py-2.5 rounded-sm">Cancel</button>}
      </div>
    </form>
  );
};

const AssignPlayersModal = ({ batch, onClose, onSaved }) => {
  const [players, setPlayers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");
  const [yobFilter, setYobFilter] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [p, b] = await Promise.all([
          api.get("/admin/players"),
          api.get(`/admin/batches/${batch.id}/players`),
        ]);
        setPlayers(p.data);
        setSelected(new Set(b.data.player_ids));
      } catch (err) { toast.error(formatApiError(err)); }
      setLoading(false);
    })();
  }, [batch.id]);

  const yobOptions = useMemo(() => {
    const years = new Set();
    players.forEach((p) => { if (p.dob) years.add(p.dob.slice(0, 4)); });
    return [...years].sort().reverse();
  }, [players]);

  const filtered = useMemo(() => players.filter((p) => {
    if (yobFilter.size > 0) {
      if (!p.dob || !yobFilter.has(p.dob.slice(0, 4))) return false;
    }
    return `${p.player_id} ${p.name} ${p.batch || ""}`.toLowerCase().includes(q.toLowerCase());
  }), [players, q, yobFilter]);

  const toggle = (pid) => {
    const next = new Set(selected);
    next.has(pid) ? next.delete(pid) : next.add(pid);
    setSelected(next);
  };
  const toggleYob = (y) => {
    const next = new Set(yobFilter);
    next.has(y) ? next.delete(y) : next.add(y);
    setYobFilter(next);
  };
  const allSelected = filtered.length > 0 && filtered.every((p) => selected.has(p.player_id));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelected) filtered.forEach((p) => next.delete(p.player_id));
    else filtered.forEach((p) => next.add(p.player_id));
    setSelected(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/batches/${batch.id}/players`, { player_ids: [...selected] });
      toast.success(`Saved — ${selected.size} player${selected.size === 1 ? "" : "s"} assigned to ${batch.name}`);
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm overflow-y-auto py-10 px-4" onClick={onClose} data-testid="assign-players-modal">
      <div onClick={(e) => e.stopPropagation()} className="max-w-2xl mx-auto bg-[#0A0A0A] border border-white/10 p-6 sm:p-8">
        <div className="flex justify-between items-start mb-5">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Assign Players</p>
            <h2 className="font-heading text-xl font-black mt-1">{batch.name}</h2>
            <p className="text-white/40 text-xs mt-1">{selected.size} selected</p>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <div className="flex gap-3 mb-3">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input placeholder="Search players…" value={q} onChange={(e) => setQ(e.target.value)} className={inputCls + " pl-9 w-full"} data-testid="assign-search" />
          </div>
          <button onClick={toggleAll} className="flex items-center gap-2 border border-[#D4AF37]/40 text-[#D4AF37] text-xs uppercase tracking-widest px-4 py-2.5 hover:bg-[#D4AF37] hover:text-black" data-testid="assign-toggle-all">
            {allSelected ? <CheckSquare size={13} /> : <Square size={13} />} {allSelected ? "Unselect all" : "Select all"}
          </button>
        </div>
        {yobOptions.length > 0 && (
          <div className="mb-3" data-testid="assign-yob-filter">
            <p className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/40 mb-2">
              <CalendarDays size={11} className="text-[#D4AF37]" /> Filter by year of birth {yobFilter.size > 0 && `· ${yobFilter.size} selected`}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {yobOptions.map((y) => (
                <button key={y} onClick={() => toggleYob(y)}
                  className={`text-[11px] uppercase tracking-widest px-2.5 py-1 border transition-colors duration-150 ${yobFilter.has(y) ? "bg-[#D4AF37] text-black border-[#D4AF37] font-bold" : "border-white/15 text-white/60 hover:border-[#D4AF37]"}`}
                  data-testid={`assign-yob-${y}`}>{y}</button>
              ))}
              {yobFilter.size > 0 && (
                <button onClick={() => setYobFilter(new Set())} className="text-[10px] uppercase tracking-widest text-white/40 hover:text-[#D4AF37] px-2 py-1" data-testid="assign-yob-clear">
                  Clear
                </button>
              )}
            </div>
          </div>
        )}
        {loading ? <p className="text-white/40 text-sm">Loading…</p> : (
          <div className="border border-white/10 max-h-[50vh] overflow-y-auto" data-testid="assign-list">
            {filtered.map((p) => (
              <label key={p.player_id} className="flex items-center gap-3 px-3 py-2.5 border-b border-white/5 hover:bg-[#D4AF37]/5 cursor-pointer" data-testid={`assign-row-${p.player_id}`}>
                <input type="checkbox" checked={selected.has(p.player_id)} onChange={() => toggle(p.player_id)} className="accent-[#D4AF37]" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{p.name}<span className="text-white/40 text-xs ml-2">{p.dob ? p.dob.slice(0, 4) : ""}</span></p>
                  <p className="text-white/40 text-xs">{p.player_id} · {p.batch || "no batch"}{p.batches && p.batches.length > 1 ? ` · ${p.batches.length} batches` : ""}</p>
                </div>
              </label>
            ))}
            {filtered.length === 0 && <p className="text-white/40 text-sm p-4">No players match.</p>}
          </div>
        )}
        <div className="flex gap-3 pt-5">
          <button onClick={save} disabled={saving} className="bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-8 py-3 rounded-sm hover:bg-[#F3D059] disabled:opacity-50" data-testid="assign-save">
            {saving ? "Saving..." : `Save (${selected.size})`}
          </button>
          <button onClick={onClose} className="border border-white/20 text-white/70 text-xs uppercase tracking-widest px-6 py-3 rounded-sm hover:border-[#D4AF37]">Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default function Centres() {
  const [centres, setCentres] = useState([]);
  const [batches, setBatches] = useState([]);
  const [centresLoaded, setCentresLoaded] = useState(false);
  const [batchesLoaded, setBatchesLoaded] = useState(false);
  const [addingCentre, setAddingCentre] = useState(false);
  const [addingBatch, setAddingBatch] = useState(false);
  const [editCentre, setEditCentre] = useState(null);
  const [editBatch, setEditBatch] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { kind: 'centre'|'batch', id, name }

  const load = () => {
    api.get("/admin/centres")
      .then((r) => { setCentres(r.data); setCentresLoaded(true); })
      .catch(() => setCentresLoaded(true));
    api.get("/admin/batches")
      .then((r) => { setBatches(r.data); setBatchesLoaded(true); })
      .catch(() => setBatchesLoaded(true));
  };
  useEffect(() => { load(); }, []);

  const saveCentre = async (f) => {
    try {
      if (editCentre?.id) await api.put(`/admin/centres/${editCentre.id}`, f);
      else await api.post("/admin/centres", f);
      toast.success("Saved");
      setEditCentre(null); setAddingCentre(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveBatch = async (f) => {
    try {
      if (editBatch?.id) await api.put(`/admin/batches/${editBatch.id}`, f);
      else await api.post("/admin/batches", f);
      toast.success("Saved");
      setEditBatch(null); setAddingBatch(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    const { kind, id } = deleteTarget;
    try {
      await api.delete(`/admin/${kind === "centre" ? "centres" : "batches"}/${id}`);
      toast.success("Removed");
      setDeleteTarget(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-centres">
      <h1 className="font-heading text-3xl font-black uppercase tracking-tight mb-8">Centres & Batches</h1>
      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold" data-testid="centres-heading">Centres {centresLoaded ? `(${centres.length})` : "…"}</h2>
            <button onClick={() => setAddingCentre(!addingCentre)} className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37]/40 px-3 py-1.5 hover:bg-[#D4AF37] hover:text-black" data-testid="centre-add-toggle">
              <Plus size={12} /> Add
            </button>
          </div>
          {addingCentre && <div className="mb-4"><CentreForm onSave={saveCentre} onCancel={() => setAddingCentre(false)} /></div>}
          <div className="space-y-2">
            {centres.map((c) => (
              editCentre?.id === c.id ? (
                <CentreForm key={c.id} initial={c} onSave={saveCentre} onCancel={() => setEditCentre(null)} />
              ) : (
                <div key={c.id} className="flex items-center justify-between border border-white/10 bg-[#0A0A0A] p-4" data-testid={`centre-${c.id}`}>
                  <div>
                    <p className="font-semibold">{c.name}</p>
                    {c.address && <p className="text-white/40 text-xs flex items-center gap-1 mt-0.5"><MapPin size={11} /> {c.address}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setEditCentre(c)} className="text-white/50 hover:text-[#D4AF37] p-1.5" data-testid={`centre-edit-${c.id}`}><Pencil size={14} /></button>
                    <button onClick={() => setDeleteTarget({ kind: "centre", id: c.id, name: c.name })} className="text-red-400/70 hover:text-red-400 p-1.5" data-testid={`centre-delete-${c.id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              )
            ))}
            {!centresLoaded && centres.length === 0 && (
              <div className="space-y-2" data-testid="centres-loading">
                {[0,1].map((i) => <div key={i} className="border border-white/10 bg-[#0A0A0A] h-16 animate-pulse" />)}
              </div>
            )}
            {centresLoaded && centres.length === 0 && <p className="text-white/40 text-sm border border-white/10 p-4 text-center" data-testid="centres-empty">No centres yet.</p>}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold" data-testid="batches-heading">Batches {batchesLoaded ? `(${batches.length})` : "…"}</h2>
            <button onClick={() => setAddingBatch(!addingBatch)} className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37]/40 px-3 py-1.5 hover:bg-[#D4AF37] hover:text-black" data-testid="batch-add-toggle">
              <Plus size={12} /> Add
            </button>
          </div>
          {addingBatch && <div className="mb-4"><BatchForm centres={centres} onSave={saveBatch} onCancel={() => setAddingBatch(false)} /></div>}
          <div className="space-y-2">
            {batches.map((b) => (
              editBatch?.id === b.id ? (
                <BatchForm key={b.id} initial={b} centres={centres} onSave={saveBatch} onCancel={() => setEditBatch(null)} />
              ) : (
                <div key={b.id} className="flex items-center justify-between border border-white/10 bg-[#0A0A0A] p-4" data-testid={`batch-${b.id}`}>
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{b.name}</p>
                    <p className="text-white/40 text-xs flex items-center gap-2 mt-0.5 flex-wrap">
                      {b.centre && <span className="flex items-center gap-1"><MapPin size={11} /> {b.centre}</span>}
                      {b.timings && <span className="flex items-center gap-1"><Clock size={11} /> {b.timings}</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => setAssignFor(b)} className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37]/40 px-3 py-1.5 hover:bg-[#D4AF37] hover:text-black" data-testid={`batch-assign-${b.id}`}>
                      <Users size={11} /> Players
                    </button>
                    <button onClick={() => setEditBatch(b)} className="text-white/50 hover:text-[#D4AF37] p-1.5" data-testid={`batch-edit-${b.id}`}><Pencil size={14} /></button>
                    <button onClick={() => setDeleteTarget({ kind: "batch", id: b.id, name: b.name })} className="text-red-400/70 hover:text-red-400 p-1.5" data-testid={`batch-delete-${b.id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              )
            ))}
            {!batchesLoaded && batches.length === 0 && (
              <div className="space-y-2" data-testid="batches-loading">
                {[0,1,2].map((i) => <div key={i} className="border border-white/10 bg-[#0A0A0A] h-16 animate-pulse" />)}
              </div>
            )}
            {batchesLoaded && batches.length === 0 && <p className="text-white/40 text-sm border border-white/10 p-4 text-center" data-testid="batches-empty">No custom batches yet.</p>}
          </div>
        </div>
      </div>

      {assignFor && <AssignPlayersModal batch={assignFor} onClose={() => setAssignFor(null)} onSaved={load} />}
      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={remove}
        title={deleteTarget?.kind === "centre" ? "Delete Centre" : "Delete Batch"}
        itemLabel={deleteTarget?.name}
        firstMessage={deleteTarget?.kind === "centre"
          ? "Deleting a centre removes it permanently. Any batches or players still tied to this centre will keep their existing values (you'll want to reassign them)."
          : "Deleting a batch removes it permanently and unassigns every player currently in this batch. Attendance records for past sessions remain but new sessions cannot be logged."}
        secondMessage={`Final check — you're about to permanently delete "${deleteTarget?.name}". This can't be undone.`}
        confirmWord="DELETE"
        testid={`confirm-delete-${deleteTarget?.kind}`}
      />
    </div>
  );
}
